import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaSearch,
  FaDownload,
  FaUpload,
  FaEdit,
  FaTrash,
  FaChartBar,
  FaTrophy,
  FaCalendar,
  FaChartLine,
  FaFilter,
} from "react-icons/fa";
import { supabase } from "../../../supabaseClient";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import { notify } from "../../UI/DesignStandards/Notifications/notify";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import { useAuth } from "../../../context/AuthContext";
import { canAccessBowlerContactInfo } from "../../../utils/authorization";
import { SkeletonBoard } from "../../UI/DesignStandards";
import { tournamentPath } from "../../../utility/tournamentUrls";
import {
  findDuplicateBowlerGroups,
  obviousOrgBowlerMatches,
} from "../../../utility/bowlerDuplicates";
import { matchReasonLabel } from "../../../utility/bowlerMatch";
import DuplicateBowlerGroups from "./DuplicateBowlerGroups";
import MergeBowlersModal from "./MergeBowlersModal";

const BowlerList = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { canManageOrganizationBowlers, canAccessContactInfo } =
    usePermissions();
  const [bowlers, setBowlers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingBowlerId, setDeletingBowlerId] = useState(null);
  const [expandedBowlerId, setExpandedBowlerId] = useState(null);
  const [timeFilter, setTimeFilter] = useState("all"); // all, 1year, 3years, 5years
  const [showAddBowler, setShowAddBowler] = useState(false);
  const [editingBowler, setEditingBowler] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [createBowlerAnyway, setCreateBowlerAnyway] = useState(false);
  const [mergePair, setMergePair] = useState(null);
  const [leavingBowlerId, setLeavingBowlerId] = useState(null);
  const [keptBowlerId, setKeptBowlerId] = useState(null);
  const mergeTimerRef = useRef(null);
  const { user } = useAuth();

  // Form state for add/edit bowler
  const [bowlerForm, setBowlerForm] = useState({
    name: "",
    usbcID: "",
    email: "",
    phone: "",
  });

  // Load bowlers data with analytics
  useEffect(() => {
    if (currentOrganization) {
      loadBowlersWithAnalytics();
    }
  }, [currentOrganization, timeFilter]);

  useEffect(() => {
    return () => {
      if (mergeTimerRef.current) clearTimeout(mergeTimerRef.current);
    };
  }, []);

  const getDateFilter = () => {
    const now = new Date();
    switch (timeFilter) {
      case "1year":
        return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      case "3years":
        return new Date(now.getFullYear() - 3, now.getMonth(), now.getDate());
      case "5years":
        return new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
      default:
        return null; // all time
    }
  };

  const loadBowlersWithAnalytics = async ({ quiet = false } = {}) => {
    if (!currentOrganization) return;

    if (!quiet) setLoading(true);
    try {
      const dateFilter = getDateFilter();

      // First, get all tournaments for this organization
      let tournamentsQuery = supabase
        .from("tournaments")
        .select(
          "id, slug, name, startDate, handicapBase, handicapPercentage, handicapMax"
        )
        .eq("organizationID", currentOrganization.id)
        .is("deletedAt", null);

      if (dateFilter) {
        tournamentsQuery = tournamentsQuery.gte(
          "startDate",
          dateFilter.toISOString()
        );
      }

      const { data: tournaments, error: tournamentsError } =
        await tournamentsQuery;

      if (tournamentsError) throw tournamentsError;

      const tournamentIds = tournaments.map((t) => t.id);

      // Get all squads for these tournaments
      const { data: squads, error: squadsError } = await supabase
        .from("squads")
        .select("id, tournamentID")
        .in("tournamentID", tournamentIds)
        .eq("isDeleted", false);

      if (squadsError) throw squadsError;

      const squadIds = squads.map((s) => s.id);

      // Get all teams for these squads
      const { data: teams, error: teamsError } = await supabase
        .from("teams")
        .select("id, squadID, teamName")
        .in("squadID", squadIds);

      if (teamsError) throw teamsError;

      const teamIds = teams.map((t) => t.id);

      // Check if user can access contact information
      const { canAccessContactInfo } = await canAccessBowlerContactInfo(
        user.id,
        currentOrganization.id
      );

      // Get all bowlers who have participated in these tournaments
      let bowlersQuery = supabase
        .from("bowlers")
        .select(
          `
          id,
          name,
          usbcID,
          created_at
        `
        )
        .eq("organizationID", currentOrganization.id)
        .is("deletedAt", null)
        .order("name", { ascending: true });

      // If user has permission, also fetch contact info
      if (canAccessContactInfo) {
        bowlersQuery = supabase
          .from("bowlers")
          .select(
            `
            id,
            name,
            usbcID,
            created_at,
            bowlersContact (
              id,
              type,
              value,
              isPrimary
            )
          `
          )
          .eq("organizationID", currentOrganization.id)
          .is("deletedAt", null)
          .order("name", { ascending: true });
      }

      const { data: bowlersData, error: bowlersError } = await bowlersQuery;

      if (bowlersError) throw bowlersError;

      // Get tournament averages for all tournaments
      const { data: averagesData, error: averagesError } = await supabase
        .from("tournamentAverages")
        .select("bowlerID, tournamentID, average")
        .in("tournamentID", tournamentIds);

      if (averagesError) throw averagesError;

      // Get verification status for averages
      const { data: verificationStates, error: verificationError } =
        await supabase
          .from("verificationStates")
          .select("entityId, isVerified")
          .eq("entityType", "averages")
          .in(
            "entityId",
            averagesData.map((avg) => avg.bowlerID)
          );

      if (verificationError) throw verificationError;

      // Get scores for all tournaments
      const { data: scoresData, error: scoresError } = await supabase
        .from("scores")
        .select("bowlerID, teamID, score, gameNumber, created")
        .in("teamID", teamIds);

      if (scoresError) throw scoresError;

      // Get team assignments to link bowlers to tournaments
      const { data: teamAssignments, error: assignmentsError } = await supabase
        .from("teamassignments")
        .select("bowlerID, teamID")
        .in("teamID", teamIds);

      if (assignmentsError) throw assignmentsError;

      // Get tournament events
      const { data: tournamentEvents, error: eventsError } = await supabase
        .from("tournamentEvents")
        .select("id, tournamentID, eventName")
        .in("tournamentID", tournamentIds);

      if (eventsError) {
        console.warn("Could not load tournament events:", eventsError);
      }

      // Process bowlers with analytics
      const bowlersWithAnalytics = bowlersData.map((bowler) => {
        // Transform contact info if available
        let email = null;
        let phone = null;

        if (canAccessContactInfo && bowler.bowlersContact) {
          const contactInfo = bowler.bowlersContact;
          email =
            contactInfo.find((c) => c.type === "email" && c.isPrimary)?.value ||
            contactInfo.find((c) => c.type === "email")?.value ||
            null;
          phone =
            contactInfo.find((c) => c.type === "phone" && c.isPrimary)?.value ||
            contactInfo.find((c) => c.type === "phone")?.value ||
            null;
        }

        // Find tournaments this bowler participated in
        const bowlerTeams = teamAssignments.filter(
          (ta) => ta.bowlerID === bowler.id
        );
        const bowlerTournaments = bowlerTeams
          .map((bt) => {
            const team = teams.find((t) => t.id === bt.teamID);
            const squad = squads.find((s) => s.id === team?.squadID);
            return tournaments.find((t) => t.id === squad?.tournamentID);
          })
          .filter(Boolean);

        // Get bowler's scores
        const bowlerScores = scoresData.filter((s) => s.bowlerID === bowler.id);

        // Calculate analytics
        const analytics = calculateBowlerAnalytics(
          bowler.id,
          bowlerTournaments,
          bowlerScores,
          averagesData,
          tournaments,
          teamAssignments,
          teams,
          scoresData,
          verificationStates
        );

        return {
          ...bowler,
          email,
          phone,
          analytics,
        };
      });

      setBowlers(bowlersWithAnalytics);
    } catch (error) {
      console.error("Error loading bowlers with analytics:", error);
      if (!quiet) notify.error("Failed to load bowlers");
    } finally {
      setLoading(false);
    }
  };

  const calculateBowlerAnalytics = (
    bowlerId,
    tournaments,
    scores,
    averages,
    allTournaments,
    teamAssignments,
    teams,
    scoresData,
    verificationStates
  ) => {
    const bowlerAverages = averages.filter((a) => a.bowlerID === bowlerId);
    const bowlerScores = scores.filter((s) => s.bowlerID === bowlerId);

    // Tournament participation
    const tournamentsBowled = tournaments.length;
    const lastTournament =
      tournaments.length > 0
        ? tournaments.sort(
            (a, b) => new Date(b.startDate) - new Date(a.startDate)
          )[0]
        : null;

    // Performance metrics
    const totalGames = bowlerScores.length;
    const totalPins = bowlerScores.reduce(
      (sum, score) => sum + (score.score || 0),
      0
    );
    const averageScore =
      totalGames > 0 ? Math.round(totalPins / totalGames) : 0;

    // High game
    const highGame =
      bowlerScores.length > 0
        ? Math.max(...bowlerScores.map((s) => s.score || 0))
        : 0;

    // Series totals (default 3-game series without per-event context)
    const SERIES_SIZE = 3; // If you want to reflect per-tournament games, supply context here
    const seriesTotals = [];
    for (let i = 0; i < bowlerScores.length; i += SERIES_SIZE) {
      const series = bowlerScores.slice(i, i + SERIES_SIZE);
      const seriesTotal = series.reduce(
        (sum, score) => sum + (score.score || 0),
        0
      );
      if (series.length === SERIES_SIZE) {
        seriesTotals.push(seriesTotal);
      }
    }
    const highSeries = seriesTotals.length > 0 ? Math.max(...seriesTotals) : 0;

    // Average comparison - only tournament averages (verified)
    // Filter averages based on verification status from the new verification system
    const verifiedAverages = bowlerAverages.filter((avg) => {
      const verification = verificationStates.find(
        (vs) => vs.entityId === avg.bowlerID
      );
      return verification?.isVerified === true;
    });
    const tournamentAverages = verifiedAverages;

    const avgEnteringAverage =
      tournamentAverages.length > 0
        ? Math.round(
            tournamentAverages.reduce((sum, avg) => sum + avg.average, 0) /
              tournamentAverages.length
          )
        : 0;

    // Recent performance (last 3 tournaments)
    const recentTournaments = tournaments
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
      .slice(0, 3);

    return {
      tournamentsBowled,
      lastTournament,
      totalGames,
      averageScore,
      highGame,
      highSeries,
      recentTournaments,
      totalPins,
      avgEnteringAverage,
      tournamentAverages: tournamentAverages.length,
    };
  };

  const deleteBowler = async (bowlerId) => {
    try {
      const { error } = await supabase
        .from("bowlers")
        .update({
          deletedAt: new Date().toISOString(),
          deletedBy: user.id,
        })
        .eq("id", bowlerId);

      if (error) throw error;

      loadBowlersWithAnalytics();
    } catch (error) {
      console.error("Error deleting bowler:", error);
      notify.error("Failed to delete bowler");
    } finally {
      setDeletingBowlerId(null);
    }
  };

  const handleBowlerFormChange = (field, value) => {
    setCreateBowlerAnyway(false);
    if (editingBowler) {
      setEditingBowler((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else {
      setBowlerForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const saveBowler = async () => {
    const bowlerData = editingBowler || bowlerForm;

    if (!bowlerData.name?.trim()) {
      notify.error("Bowler name is required");
      return;
    }

    if (
      !editingBowler &&
      !createBowlerAnyway &&
      obviousOrgBowlerMatches(bowlerData, bowlers).length > 0
    ) {
      notify.error("This matches a bowler already on Bowlers.");
      return;
    }

    try {
      // Check if user can access contact information
      const { canAccessContactInfo } = await canAccessBowlerContactInfo(
        user.id,
        currentOrganization.id
      );

      if (editingBowler) {
        // Update bowler basic info
        const { data: updatedBowler, error: bowlerError } = await supabase
          .from("bowlers")
          .update({
            name: bowlerData.name.trim(),
            usbcID: bowlerData.usbcID?.trim() || null,
            updatedAt: new Date().toISOString(),
            updatedBy: user.id,
          })
          .eq("id", editingBowler.id);

        if (bowlerError) throw bowlerError;

        // Update contact info if user has permission
        if (canAccessContactInfo) {
          // Soft delete existing contact info
          await supabase
            .from("bowlersContact")
            .update({
              deletedAt: new Date().toISOString(),
              deletedBy: user.id,
            })
            .eq("bowlerID", editingBowler.id)
            .in("type", ["email", "phone"])
            .is("deletedAt", null);

          // Insert new contact info
          const contactEntries = [];
          if (bowlerData.email?.trim()) {
            contactEntries.push({
              bowlerID: editingBowler.id,
              type: "email",
              value: bowlerData.email.trim(),
              isPrimary: true,
              createdBy: user.id,
              created: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }
          if (bowlerData.phone?.trim()) {
            contactEntries.push({
              bowlerID: editingBowler.id,
              type: "phone",
              value: bowlerData.phone.trim(),
              isPrimary: true,
              createdBy: user.id,
              created: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }

          if (contactEntries.length > 0) {
            const { error: contactError } = await supabase
              .from("bowlersContact")
              .insert(contactEntries);

            if (contactError) {
              console.error("Error updating contact info:", contactError);
            }
          }
        }

        setEditingBowler(null);
      } else {
        // Insert bowler basic info
        const { data: newBowler, error: bowlerError } = await supabase
          .from("bowlers")
          .insert({
            name: bowlerData.name.trim(),
            usbcID: bowlerData.usbcID?.trim() || null,
            organizationID: currentOrganization.id,
            createdBy: user.id,
            updatedBy: user.id,
          })
          .select()
          .single();

        if (bowlerError) throw bowlerError;

        // Insert contact info if user has permission
        if (canAccessContactInfo && newBowler) {
          const contactEntries = [];
          if (bowlerData.email?.trim()) {
            contactEntries.push({
              bowlerID: newBowler.id,
              type: "email",
              value: bowlerData.email.trim(),
              isPrimary: true,
              createdBy: user.id,
              created: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }
          if (bowlerData.phone?.trim()) {
            contactEntries.push({
              bowlerID: newBowler.id,
              type: "phone",
              value: bowlerData.phone.trim(),
              isPrimary: true,
              createdBy: user.id,
              created: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }

          if (contactEntries.length > 0) {
            const { error: contactError } = await supabase
              .from("bowlersContact")
              .insert(contactEntries);

            if (contactError) {
              console.error("Error inserting contact info:", contactError);
            }
          }
        }
      }

      // Reset form and reload data
      setBowlerForm({
        name: "",
        usbcID: "",
        email: "",
        phone: "",
      });
      setShowAddBowler(false);
      loadBowlersWithAnalytics();
    } catch (error) {
      console.error("Error saving bowler:", error);
      notify.error("Failed to save bowler");
    }
  };

  const cancelBowlerForm = () => {
    setShowAddBowler(false);
    setEditingBowler(null);
    setCreateBowlerAnyway(false);
    setBowlerForm({
      name: "",
      usbcID: "",
      email: "",
      phone: "",
    });
  };

  const exportBowlers = async () => {
    setExporting(true);
    try {
      const exportData = bowlers.map((bowler) => ({
        name: bowler.name,
        usbcID: bowler.usbcID || "",
        email: bowler.email || "",
        phone: bowler.phone || "",
        tournamentsBowled: bowler.analytics?.tournamentsBowled || 0,
        totalGames: bowler.analytics?.totalGames || 0,
        averageScore: bowler.analytics?.averageScore || 0,
        highGame: bowler.analytics?.highGame || 0,
        highSeries: bowler.analytics?.highSeries || 0,
        lastTournament: bowler.analytics?.lastTournament || "",
      }));

      const csvContent = [
        // Header row
        [
          "Name",
          "USBC ID",
          "Email",
          "Phone",
          "Tournaments",
          "Games",
          "Average",
          "High Game",
          "High Series",
          "Last Tournament",
        ],
        // Data rows
        ...exportData.map((row) => [
          row.name,
          row.usbcID,
          row.email,
          row.phone,
          row.tournamentsBowled,
          row.totalGames,
          row.averageScore,
          row.highGame,
          row.highSeries,
          row.lastTournament,
        ]),
      ]
        .map((row) => row.map((cell) => `"${cell}"`).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${currentOrganization.name}_bowlers_${
          new Date().toISOString().split("T")[0]
        }.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notify.success("Bowlers exported successfully");
    } catch (error) {
      console.error("Error exporting bowlers:", error);
      notify.error("Failed to export bowlers");
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "text/csv") {
      setImportFile(file);
    } else {
      notify.error("Please select a valid CSV file");
    }
  };

  const downloadExampleCSV = () => {
    const csvContent = [
      ["Name", "USBC ID", "Email", "Phone"],
      ["John Doe", "12345678", "john.doe@example.com", "(555) 123-4567"],
      ["Jane Smith", "87654321", "jane.smith@example.com", "(555) 987-6543"],
      ["Bob Johnson", "11223344", "bob.johnson@example.com", "(555) 456-7890"],
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "bowler_import_example.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notify.success("Example CSV downloaded");
  };

  const importBowlers = async () => {
    if (!importFile) {
      notify.error("Please select a file to import");
      return;
    }

    setImporting(true);
    try {
      const text = await importFile.text();
      const lines = text.split("\n");
      const headers = lines[0]
        .split(",")
        .map((h) => h.replace(/"/g, "").trim());

      const bowlersToImport = lines
        .slice(1)
        .filter((line) => line.trim())
        .map((line) => {
          const values = line.split(",").map((v) => v.replace(/"/g, "").trim());
          const bowler = {};
          headers.forEach((header, index) => {
            bowler[header.toLowerCase().replace(/\s+/g, "")] =
              values[index] || "";
          });
          return bowler;
        });

      // Validate required fields
      const validBowlers = bowlersToImport.filter((bowler) => bowler.name);
      if (validBowlers.length === 0) {
        notify.error("No valid bowlers found in the file");
        return;
      }

      // Use secure Edge Function for import
      const { data, error } = await supabase.functions.invoke(
        "import-bowlers",
        {
          body: {
            organizationId: currentOrganization.id,
            bowlers: validBowlers.map((bowler) => ({
              name: bowler.name,
              usbcID: bowler.usbcid || bowler.usbcID || null,
              email: bowler.email || null,
              phone: bowler.phone || null,
            })),
          },
        }
      );

      if (error) throw error;

      if (data.validationErrors && data.validationErrors.length > 0) {
        notify.warning(
          `Imported ${data.importedCount} bowlers with ${data.validationErrors.length} validation errors`
        );
        console.warn("Validation errors:", data.validationErrors);
      } else {
        notify.success(`${data.importedCount} bowlers imported successfully`);
      }
      setShowImportModal(false);
      setImportFile(null);
      loadBowlersWithAnalytics();
    } catch (error) {
      console.error("Error importing bowlers:", error);
      notify.error("Failed to import bowlers");
    } finally {
      setImporting(false);
    }
  };

  // Filter bowlers based on search query
  const duplicateGroups = useMemo(
    () => findDuplicateBowlerGroups(bowlers),
    [bowlers]
  );
  const addBowlerMatches = useMemo(
    () => (showAddBowler ? obviousOrgBowlerMatches(bowlerForm, bowlers) : []),
    [bowlerForm, bowlers, showAddBowler]
  );

  const filteredBowlers = bowlers.filter(
    (bowler) =>
      bowler.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bowler.usbcID?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bowler.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-4" role="status" aria-label="Loading bowlers">
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={6} />
      </div>
    );
  }

  // Check permissions
  if (!canManageOrganizationBowlers) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-brand-cream rounded-lg flex items-center justify-center mx-auto mb-4 border border-brand-charcoal/10">
            <svg
              className="w-8 h-8 text-brand-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="heading-display text-2xl mb-2">Access Denied</h2>
          <p className="text-brand-muted">
            You don't have permission to manage organization bowlers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="card-board overflow-hidden mb-6">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h2 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Organization Bowlers
            </h2>
            <p className="text-sm text-brand-mist mt-1">
              Names, USBC IDs, and tournament averages.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setShowImportModal(true)}
              className="inline-flex items-center min-h-11 px-3 py-2 text-sm text-brand-charcoal bg-brand-cream font-display font-bold tracking-wide border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
            >
              <FaUpload className="mr-2 h-4 w-4" />
              <span>Import</span>
            </button>
            <button
              type="button"
              onClick={exportBowlers}
              disabled={exporting}
              className="inline-flex items-center min-h-11 px-3 py-2 text-sm text-brand-charcoal bg-brand-cream font-display font-bold tracking-wide border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaDownload className="mr-2 h-4 w-4" />
              <span>{exporting ? "Exporting..." : "Export"}</span>
            </button>
            <button
              type="button"
              onClick={() => setShowDuplicates((open) => !open)}
              className="inline-flex items-center min-h-11 px-3 py-2 text-sm text-brand-charcoal bg-brand-cream font-display font-bold tracking-wide border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
            >
              <span>{showDuplicates ? "Hide duplicates" : "Find duplicates"}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setCreateBowlerAnyway(false);
                setShowAddBowler(true);
              }}
              className="inline-flex items-center min-h-11 px-3 py-2 text-sm text-brand-charcoal bg-brand-cream font-display font-bold tracking-wide border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
            >
              <FaPlus className="mr-2 h-4 w-4" />
              <span>Add Bowler</span>
            </button>
          </div>
        </div>
        <div className="p-4 sm:p-6">

        {/* Error Display */}
        {error && (
          <InlineAlert
            type="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Import Section */}
        {showImportModal && (
          <div className="card-inset p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
              <h3 className="text-lg font-medium text-brand-charcoal">
                Import Bowlers
              </h3>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                }}
                className="text-brand-muted hover:text-brand-charcoal"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-brand-charcoal mb-2">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportFile}
                    className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                  />
                  <p className="text-xs text-brand-muted mt-1">
                    CSV should have columns: Name, USBC ID, Email, Phone
                  </p>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={downloadExampleCSV}
                    className="px-3 py-2 text-sm font-display font-bold text-brand-charcoal bg-brand-cream border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
                  >
                    Download Example
                  </button>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="px-3 py-2 text-sm font-display font-bold text-brand-charcoal bg-brand-cream border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
                >
                  Cancel
                </button>
                <button
                  onClick={importBowlers}
                  disabled={!importFile || importing}
                  className="px-3 py-2 text-sm font-display font-bold text-brand-charcoal bg-brand-gold rounded-lg hover:bg-brand-gold/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? "Importing..." : "Import"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Bowler Form */}
        {showAddBowler && (
          <div className="card-inset p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
              <h3 className="text-lg font-medium text-brand-charcoal">
                Add New Bowler
              </h3>
              <button
                onClick={cancelBowlerForm}
                className="text-brand-muted hover:text-brand-charcoal"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={bowlerForm.name}
                  onChange={(e) =>
                    handleBowlerFormChange("name", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                  placeholder="Enter bowler name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  USBC ID
                </label>
                <input
                  type="text"
                  value={bowlerForm.usbcID}
                  onChange={(e) =>
                    handleBowlerFormChange("usbcID", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                  placeholder="Enter USBC ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={bowlerForm.email}
                  onChange={(e) =>
                    handleBowlerFormChange("email", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={bowlerForm.phone}
                  onChange={(e) =>
                    handleBowlerFormChange("phone", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                  placeholder="Enter phone number"
                />
              </div>
            </div>
            {addBowlerMatches.length > 0 ? (
              <div className="mt-4 space-y-2">
                <p className="text-sm text-brand-charcoal">
                  These bowlers already match. Pick one from the list, or create a new bowler anyway.
                </p>
                <ul className="text-sm text-brand-muted space-y-1">
                  {addBowlerMatches.map((match) => {
                    const reason = matchReasonLabel(match.matchReason);
                    return (
                      <li key={match.id}>
                        {match.name}
                        {match.usbcID ? ` · USBC ID ${match.usbcID}` : ""}
                        {reason ? ` · ${reason}` : ""}
                      </li>
                    );
                  })}
                </ul>
                <label className="flex items-center gap-2 text-sm text-brand-charcoal">
                  <input
                    type="checkbox"
                    checked={createBowlerAnyway}
                    onChange={(event) => setCreateBowlerAnyway(event.target.checked)}
                  />
                  Create a new bowler anyway
                </label>
              </div>
            ) : null}
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={cancelBowlerForm}
                className="px-3 py-2 text-sm font-display font-bold text-brand-charcoal bg-brand-cream border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
              >
                Cancel
              </button>
              <button
                onClick={saveBowler}
                disabled={addBowlerMatches.length > 0 && !createBowlerAnyway}
                className="px-3 py-2 text-sm font-display font-bold text-brand-charcoal bg-brand-gold rounded-lg hover:bg-brand-gold/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Bowler
              </button>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-4 w-4 sm:h-5 sm:w-5 text-brand-muted" />
            </div>
            <input
              type="text"
              className="focus:ring-brand-gold-ink focus:border-brand-gold-ink block w-full pl-9 sm:pl-10 text-sm border border-brand-charcoal/20 rounded-lg py-2"
              placeholder="Search bowlers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex items-center space-x-2">
            <FaFilter className="h-4 w-4 text-brand-muted" />
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="border border-brand-charcoal/20 rounded-lg px-2 sm:px-3 py-2 text-sm focus:ring-brand-gold-ink focus:border-brand-gold-ink"
            >
              <option value="all">All Time</option>
              <option value="1year">Last Year</option>
              <option value="3years">Last 3 Years</option>
              <option value="5years">Last 5 Years</option>
            </select>
          </div>
        </div>
        </div>
      </div>

      {showDuplicates ? (
        <div className="card-board p-4 sm:p-6">
          <h3 className="text-lg font-medium text-brand-charcoal mb-3">
            Likely duplicates
          </h3>
          <DuplicateBowlerGroups
            groups={duplicateGroups}
            onMerge={setMergePair}
          />
        </div>
      ) : null}

      {/* Bowlers List */}
      <div className="card-board p-4 sm:p-6 mb-6">
        {filteredBowlers.length === 0 ? (
          <div className="text-center py-8 text-brand-muted">
            <svg
              className="text-4xl mx-auto mb-4 text-brand-mist"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p>No bowlers found.</p>
            <p className="text-sm mt-2">
              {searchQuery
                ? "Try adjusting your search terms."
                : "Bowlers will appear here once they participate in tournaments."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredBowlers.map((bowler) => {
              const leaving = leavingBowlerId === bowler.id;
              const kept = keptBowlerId === bowler.id;
              return (
              <div
                key={bowler.id}
                className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none ${
                  leaving ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100"
                }`}
              >
              <div className="overflow-hidden">
              <div
                className={`border border-brand-charcoal/10 rounded-lg overflow-hidden transition-colors duration-500 ${
                  kept ? "bg-brand-cream" : ""
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 hover:bg-brand-cream/50 transition-colors">
                  <div className="flex-1 mb-3 sm:mb-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                      <div className="flex items-center">
                        <svg
                          className="w-4 h-4 text-brand-muted mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <span className="font-medium text-brand-charcoal text-base">
                          {bowler.name}
                        </span>
                      </div>
                      {bowler.usbcID && (
                        <div className="text-sm text-brand-muted ml-6 sm:ml-0">
                          USBC: {bowler.usbcID}
                        </div>
                      )}
                      {bowler.email && (
                        <a
                          href={`mailto:${bowler.email}`}
                          className="text-sm text-brand-gold-ink hover:text-brand-charcoal hover:underline ml-6 sm:ml-0 break-all"
                        >
                          {bowler.email}
                        </a>
                      )}
                      {bowler.phone && (
                        <a
                          href={`tel:${bowler.phone}`}
                          className="text-sm text-brand-gold-ink hover:text-brand-charcoal hover:underline ml-6 sm:ml-0"
                        >
                          {bowler.phone.replace(
                            /(\d{3})(\d{3})(\d{4})/,
                            "($1) $2-$3"
                          )}
                        </a>
                      )}
                    </div>

                    {/* Performance Summary */}
                    <div className="mt-2 flex flex-wrap items-center gap-3 sm:gap-6 text-xs text-brand-muted">
                      <div className="flex items-center">
                        <FaTrophy className="w-3 h-3 mr-1" />
                        <span>
                          {bowler.analytics?.tournamentsBowled || 0} tournaments
                        </span>
                      </div>
                      {bowler.analytics?.lastTournament && (
                        <div className="flex items-center">
                          <FaCalendar className="w-3 h-3 mr-1" />
                          <span>
                            Last:{" "}
                            {new Date(
                              bowler.analytics.lastTournament.startDate
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end sm:justify-start space-x-2 mt-3 sm:mt-0">
                    <button
                      type="button"
                      onClick={() =>
                        setMergePair({ keepId: bowler.id, absorbId: null })
                      }
                      className="px-3 py-2 text-sm font-display font-bold text-brand-charcoal bg-brand-cream border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
                    >
                      Merge
                    </button>
                    <button
                      onClick={() =>
                        setExpandedBowlerId(
                          expandedBowlerId === bowler.id ? null : bowler.id
                        )
                      }
                      className="p-2 text-brand-charcoal hover:text-brand-gold-ink focus:outline-none focus:ring-2 focus:ring-brand-gold-ink focus:ring-inset text-sm touch-manipulation"
                      title={`${
                        expandedBowlerId === bowler.id ? "Hide" : "View"
                      } ${bowler.name} analytics`}
                      aria-label={`${
                        expandedBowlerId === bowler.id ? "Hide" : "View"
                      } ${bowler.name} analytics`}
                    >
                      <FaChartBar className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setEditingBowler(bowler)}
                      className="p-2 text-brand-muted hover:text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink focus:ring-inset text-sm touch-manipulation"
                      title={`Edit ${bowler.name}`}
                      aria-label={`Edit ${bowler.name}`}
                    >
                      <FaEdit className="w-5 h-5" />
                    </button>
                    {/* Delete button temporarily hidden
                    {deletingBowlerId === bowler.id ? (
                      <div
                        className="flex items-center space-x-2"
                        role="group"
                        aria-label="Delete confirmation"
                      >
                        <span className="text-xs text-red-600 whitespace-nowrap">
                          Delete?
                        </span>
                        <button
                          onClick={() => deleteBowler(bowler.id)}
                          className="px-2 py-1 text-red-600 hover:text-red-800 focus:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-inset text-xs font-medium touch-manipulation"
                          disabled={deletingBowlerId === bowler.id}
                          aria-label={`Confirm delete ${bowler.name}`}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setDeletingBowlerId(null)}
                          className="px-2 py-1 text-gray-600 hover:text-gray-800 focus:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-inset text-xs font-medium touch-manipulation"
                          aria-label={`Cancel delete ${bowler.name}`}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeletingBowlerId(bowler.id)}
                        className="p-2 text-red-600 hover:text-red-800 focus:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-inset text-sm touch-manipulation"
                        title={`Delete ${bowler.name}`}
                        aria-label={`Delete ${bowler.name}`}
                      >
                        <FaTrash className="w-5 h-5" />
                      </button>
                    )}
                    */}
                  </div>
                </div>

                {/* Edit Bowler Form */}
                {editingBowler?.id === bowler.id && (
                  <div className="border-t border-brand-charcoal/10 card-inset p-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
                      <h4 className="text-sm font-medium text-brand-charcoal">
                        Edit {bowler.name}
                      </h4>
                      <button
                        onClick={cancelBowlerForm}
                        className="text-brand-muted hover:text-brand-charcoal"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-brand-charcoal mb-1">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={editingBowler.name}
                          onChange={(e) =>
                            handleBowlerFormChange("name", e.target.value)
                          }
                          className="w-full px-2 py-1 text-sm border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                          placeholder="Enter bowler name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-brand-charcoal mb-1">
                          USBC ID
                        </label>
                        <input
                          type="text"
                          value={editingBowler.usbcID || ""}
                          onChange={(e) =>
                            handleBowlerFormChange("usbcID", e.target.value)
                          }
                          className="w-full px-2 py-1 text-sm border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                          placeholder="Enter USBC ID"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-brand-charcoal mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={editingBowler.email || ""}
                          onChange={(e) =>
                            handleBowlerFormChange("email", e.target.value)
                          }
                          className="w-full px-2 py-1 text-sm border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                          placeholder="Enter email address"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-brand-charcoal mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={editingBowler.phone || ""}
                          onChange={(e) =>
                            handleBowlerFormChange("phone", e.target.value)
                          }
                          className="w-full px-2 py-1 text-sm border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2 mt-3">
                      <button
                        onClick={cancelBowlerForm}
                        className="px-3 py-1 text-xs font-display font-bold text-brand-charcoal bg-brand-cream border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveBowler}
                        className="px-3 py-1 text-xs font-display font-bold text-brand-charcoal bg-brand-gold rounded-lg hover:bg-brand-gold/90"
                      >
                        Update Bowler
                      </button>
                    </div>
                  </div>
                )}

                {/* Expanded Analytics Section */}
                {expandedBowlerId === bowler.id && (
                  <div className="border-t border-brand-charcoal/10 bg-brand-cream/80 p-4">
                    <h4 className="text-sm font-medium text-brand-charcoal mb-3">
                      Performance Analytics for {bowler.name}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-sm">
                      <div className="bg-white p-3 rounded-lg border border-brand-charcoal/10">
                        <div className="flex items-center mb-2">
                          <FaTrophy className="w-4 h-4 text-brand-muted mr-2" />
                          <span className="font-medium text-brand-charcoal">
                            Tournaments
                          </span>
                        </div>
                        <div className="text-2xl font-score font-semibold text-brand-charcoal">
                          {bowler.analytics?.tournamentsBowled || 0}
                        </div>
                        <div className="text-xs text-brand-muted">
                          Total participated
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-brand-charcoal/10">
                        <div className="flex items-center mb-2">
                          <FaChartLine className="w-4 h-4 text-brand-muted mr-2" />
                          <span className="font-medium text-brand-charcoal">
                            Tournament Avg
                          </span>
                        </div>
                        <div className="text-2xl font-score font-semibold text-brand-charcoal">
                          {bowler.analytics?.averageScore || 0}
                        </div>
                        <div className="text-xs text-brand-muted">
                          Overall average
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-brand-charcoal/10">
                        <div className="flex items-center mb-2">
                          <FaChartLine className="w-4 h-4 text-brand-muted mr-2" />
                          <span className="font-medium text-brand-charcoal">
                            High Game
                          </span>
                        </div>
                        <div className="text-2xl font-score font-semibold text-brand-charcoal">
                          {bowler.analytics?.highGame || 0}
                        </div>
                        <div className="text-xs text-brand-muted">
                          Best single game
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-brand-charcoal/10">
                        <div className="flex items-center mb-2">
                          <FaChartLine className="w-4 h-4 text-brand-muted mr-2" />
                          <span className="font-medium text-brand-charcoal">
                            High Series
                          </span>
                        </div>
                        <div className="text-2xl font-score font-semibold text-brand-charcoal">
                          {bowler.analytics?.highSeries || 0}
                        </div>
                        <div className="text-xs text-brand-muted">
                          Best 3-game series
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-brand-charcoal/10">
                        <div className="flex items-center mb-2">
                          <FaCalendar className="w-4 h-4 text-brand-muted mr-2" />
                          <span className="font-medium text-brand-charcoal">
                            Games Bowled
                          </span>
                        </div>
                        <div className="text-2xl font-score font-semibold text-brand-charcoal">
                          {bowler.analytics?.totalGames || 0}
                        </div>
                        <div className="text-xs text-brand-muted">Total games</div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-brand-charcoal/10">
                        <div className="flex items-center mb-2">
                          <FaChartLine className="w-4 h-4 text-brand-muted mr-2" />
                          <span className="font-medium text-brand-charcoal">
                            Avg Entering Avg
                          </span>
                        </div>
                        <div className="text-2xl font-score font-semibold text-brand-charcoal">
                          {bowler.analytics?.avgEnteringAverage || 0}
                        </div>
                        <div className="text-xs text-brand-muted">
                          {bowler.analytics?.tournamentAverages || 0}{" "}
                          tournaments
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-lg border border-brand-charcoal/10">
                        <div className="flex items-center mb-2">
                          <FaChartLine className="w-4 h-4 text-brand-muted mr-2" />
                          <span className="font-medium text-brand-charcoal">
                            Total Pins
                          </span>
                        </div>
                        <div className="text-2xl font-score font-semibold text-brand-charcoal">
                          {bowler.analytics?.totalPins || 0}
                        </div>
                        <div className="text-xs text-brand-muted">
                          Lifetime total
                        </div>
                      </div>
                    </div>

                    {/* Recent Tournaments */}
                    {bowler.analytics?.recentTournaments &&
                      bowler.analytics.recentTournaments.length > 0 && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium text-brand-charcoal mb-2">
                            Recent Tournaments
                          </h5>
                          <div className="space-y-2">
                            {bowler.analytics.recentTournaments.map(
                              (tournament, index) => (
                                <div
                                  key={index}
                                  className="text-xs text-brand-muted flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0"
                                >
                                  <button
                                    onClick={() =>
                                      navigate(tournamentPath(tournament))
                                    }
                                    className="text-brand-gold-ink hover:text-brand-charcoal hover:underline focus:outline-none focus:ring-2 focus:ring-brand-gold-ink focus:ring-inset rounded text-left touch-manipulation"
                                  >
                                    {tournament.name}
                                  </button>
                                  <span className="text-brand-muted">
                                    {new Date(
                                      tournament.startDate
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
              </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      <MergeBowlersModal
        isOpen={Boolean(mergePair)}
        bowlers={bowlers}
        initialKeepId={mergePair?.keepId}
        initialAbsorbId={mergePair?.absorbId}
        onClose={() => setMergePair(null)}
        onMerged={({ absorbId, keepId, usbcID }) => {
          setMergePair(null);
          setExpandedBowlerId((current) =>
            current === absorbId ? null : current
          );
          setEditingBowler((current) =>
            current?.id === absorbId ? null : current
          );
          setKeptBowlerId(keepId);
          setBowlers((current) =>
            current.map((bowler) =>
              bowler.id === keepId && usbcID && !bowler.usbcID
                ? { ...bowler, usbcID }
                : bowler
            )
          );
          const motionMs = window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? 0
            : 200;
          if (mergeTimerRef.current) clearTimeout(mergeTimerRef.current);
          window.requestAnimationFrame(() => setLeavingBowlerId(absorbId));
          mergeTimerRef.current = setTimeout(() => {
            setBowlers((current) =>
              current.filter((bowler) => bowler.id !== absorbId)
            );
            setLeavingBowlerId(null);
            loadBowlersWithAnalytics({ quiet: true });
            mergeTimerRef.current = setTimeout(() => setKeptBowlerId(null), 700);
          }, motionMs);
        }}
      />
    </div>
  );
};

export default BowlerList;
